<template>
  <div
    v-if="message.user && userDetails"
    :id="message.id"
    :class="[
      'message',
      {
        'same-as-prev': isSameAsPrevUser,
        'current-user': isThisUser,
        'is-underlined': isMessageHighlit,
        'is-deleting': isDeleting,
      },
    ]"
  >
    <div class="timestamp">{{ format(new Date(message.createdAt), 'HH:mm') }}</div>
    <div class="content">
      <div v-if="message.user !== undefined" class="sender">@{{ message.user?.username }}</div>
      <div v-else class="sender">Unknown</div>
      <div class="body-container">
        <div class="body">
          {{ message.body }}
        </div>

        <GamIcon v-if="canEdit" class="delete" :name="GamIconName.TRASH" @click="handleDelete" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MessageDto } from '@/core/data/chat/chat.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useChatStore } from '@/stores/chat.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { format } from 'date-fns';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

export interface MessageProps {
  message: MessageDto;
  isSameAsPrev?: boolean;
  prevUsername?: string;
  isEven?: boolean;
  canEdit?: boolean;
}

const { message, prevUsername } = defineProps<MessageProps>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);
const chatStore = useChatStore();
const route = useRoute();

const isDeleting = ref<boolean>(false);

const isMessageHighlit = computed(() => message?.id === route.query.message);
const isSameAsPrevUser = computed(() => message.user?.username === prevUsername);
const isThisUser = computed(() => message.user?.username === userDetails.value?.username);

const handleDelete = () => {
  if (isDeleting.value) return;

  isDeleting.value = true;

  setTimeout(async () => {
    if (window.confirm('Are you sure you want to unsend this message?')) {
      await chatStore.deleteMessage(message.chatId, message.id);
    }

    isDeleting.value = false;
  }, 300);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.message {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;

  &.same-as-prev {
    margin-top: calc(var(--spacing-md) * -4);
  }
}

.content {
  display: flex;
  flex-direction: column;
}

.sender {
  font-weight: 700;
  font-size: calc(14 / 16 * 1rem);
  line-height: 1.5rem;
  color: var(--color-white-100);
  letter-spacing: 0.03em;
}
.message:not(.current-user) .sender {
  color: rgb(160, 160, 160);
}
.same-as-prev .sender {
  opacity: 0;
  pointer-events: none;
}

.body-container {
  display: flex;
  flex-shrink: 1;
  position: relative;
  gap: var(--spacing-l);
  padding-right: var(--spacing-xxl);
}

.body {
  font-weight: 300;
  font-size: calc(14 / 16 * 1rem);
  line-height: 1.5rem;
  letter-spacing: 0.02em;
  transform: translateX(2px);
  color: var(--color-white-100);
  display: flex;
  flex-shrink: 1;
  transition: color var(--transition);
  // text-wrap: balance;
}
.message:not(.current-user) .body {
  color: rgb(160, 160, 160);
}
.message:not(.current-user).is-underlined .body {
  color: var(--color-pink);
}
.message.is-deleting .body {
  opacity: 0.5;
}

.delete {
  display: block;
  position: absolute;
  top: 3px;
  right: 0;
  opacity: 0;
  position: relative;
}
@include hover-capable {
  .body-container:hover .delete {
    opacity: 0.3;
    cursor: point;
  }
}
@include hover-capable {
  .body-container:hover .delete:hover {
    opacity: 1;
  }
}

.timestamp {
  font-size: calc(11 / 16 * 1rem);
  line-height: 1.5rem;
  color: var(--color-white-30);
  transform: translateY(1px);
  letter-spacing: 0.1em;
  width: 3rem;
  flex-shrink: 0;
}

.same-as-prev .timestamp {
  opacity: 0;
}
</style>
